import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Container } from "../components/ui/Container";
import { Stack } from "../components/ui/Stack";
import { Heading } from "../components/ui/Heading";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Spinner } from "../components/ui/Spinner";
import { Alert } from "../components/ui/Alert";
import { invitationApi } from "../services/invitationApi";
import { getTranslatedError } from "../utils/errorUtils";
import authApi from "services/authService";

export const AcceptInvitation: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [invitation, setInvitation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (token) {
      fetchInvitation();
    } else {
      setError(t("admin.invitations.errors.tokenRequired"));
      setLoading(false);
    }
  }, [token]);

  const fetchInvitation = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const response = await invitationApi.getInvitationByToken(token!);
      setInvitation(response.invitation);
    } catch (err: any) {
      setError(getTranslatedError(err, t, "admin.invitations.errors.fetch"));
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (): Promise<void> => {
    if (!formData.name.trim()) {
      setError(t("admin.invitations.errors.nameRequired"));
      return;
    }

    if (!formData.username.trim()) {
      setError(t("admin.invitations.errors.usernameRequired"));
      return;
    }

    if (formData.username.length < 3) {
      setError(t("admin.invitations.errors.usernameTooShort"));
      return;
    }

    if (!formData.password) {
      setError(t("admin.invitations.errors.passwordRequired"));
      return;
    }

    if (formData.password.length < 8) {
      setError(t("admin.invitations.errors.passwordTooShort"));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t("admin.invitations.errors.passwordMismatch"));
      return;
    }

    try {
      setAccepting(true);
      setError(null);
      await invitationApi.acceptInvitation({
        token: token!,
        name: formData.name.trim(),
        username: formData.username.trim(),
        password: formData.password,
      });

      // Auto-login after accepting invitation
      try {
        // Use test captcha token for invitation acceptance (internal flow)
        const loginResponse = await authApi.login({
          usernameOrEmail: formData.username.trim(),
          password: formData.password,
          captchaValue: "test-token-invitation",
        });
        localStorage.setItem("token", loginResponse.token);
        navigate("/admin/dashboard");
      } catch (loginError: any) {
        // Invitation accepted but login failed - redirect to login page
        navigate("/login", {
          state: { message: t("admin.invitations.acceptSuccess") },
        });
      }
    } catch (err: any) {
      setError(getTranslatedError(err, t, "admin.invitations.errors.accept"));
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Stack align="center" spacing="lg">
          <Spinner size="lg" />
        </Stack>
      </Container>
    );
  }

  if (error && !invitation) {
    return (
      <Container>
        <Stack spacing="lg">
          <Heading size="lg">{t("admin.invitations.acceptInvitation")}</Heading>
          <Alert variant="error">{error}</Alert>
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <div className="max-w-md mx-auto">
        <Stack spacing="lg">
          <Heading size="lg" className="text-center">
            {t("admin.invitations.acceptInvitation")}
          </Heading>

          {invitation && (
            <Card padding="lg">
              <Stack spacing="md">
                <div className="text-center mb-4">
                  <p className="text-lg font-semibold">{invitation.storeName}</p>
                  <p className="text-sm text-gray-600">{t("admin.invitations.invitedTo")}</p>
                  <p className="text-sm text-gray-600">{invitation.email}</p>
                </div>

                {error && (
                  <Alert variant="error" onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                <Input
                  label={t("admin.invitations.name")}
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder={t("admin.invitations.namePlaceholder")}
                />

                <Input
                  label={t("admin.invitations.username")}
                  type="text"
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  placeholder={t("admin.invitations.usernamePlaceholder")}
                />

                <Input
                  label={t("admin.invitations.password")}
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder={t("admin.invitations.passwordPlaceholder")}
                />

                <Input
                  label={t("admin.invitations.confirmPassword")}
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData({ ...formData, confirmPassword: e.target.value })
                  }
                  placeholder={t("admin.invitations.confirmPasswordPlaceholder")}
                />

                <Button
                  variant="primary"
                  fullWidth
                  onClick={handleAccept}
                  disabled={accepting}
                >
                  {accepting ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      {t("admin.invitations.accepting")}
                    </>
                  ) : (
                    t("admin.invitations.acceptButton")
                  )}
                </Button>
              </Stack>
            </Card>
          )}
        </Stack>
      </div>
    </Container>
  );
};
